import { createMarketplaceFeedSource } from "@/services/marketplaceFeedSource";
import {
  isMediaAuthorityReadsFeatureEnabled,
} from "@/config/runtimeConfig";
import { mediaAuthorityService } from "@/services/mediaAuthorityService";

const marketplaceFeedSource = createMarketplaceFeedSource();

const clampBatchSize = (value) =>
  Math.min(24, Math.max(1, Number.isSafeInteger(value) ? value : 12));

const normalizeCursor = (value) =>
  typeof value === "string" && value.length > 0 ? value : null;

const normalizeSearch = (value) =>
  typeof value === "string" ? value.trim().toLocaleLowerCase() : "";

const matchesSearch = (item, search) =>
  !search ||
  [item.itemName, item.itemDescription, item.organisationName]
    .filter((value) => typeof value === "string")
    .some((value) => value.toLocaleLowerCase().includes(search));

export function mergeTimelinePresentations(itemPresentations, mediaPresentations) {
  const mediaItems = mediaPresentations.map((item) => ({
    ...item,
    id: item.id || item.authorityMediaId,
    _authorityPresentation: true,
  }));
  const mediaIdentities = new Set(
    mediaItems.map((item) => item.id || item.itemId).filter(Boolean),
  );
  const seen = new Set();
  return [
    ...itemPresentations.filter(
      (item) => !mediaIdentities.has(item.id || item.itemId),
    ),
    ...mediaItems,
  ].filter((item) => {
    const identity = item.id || item.itemId;
    if (!identity || seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
}

const profileAuthorityRequired = () => {
  const error = new Error(
    "Profile items require the bounded profile presentation service."
  );
  error.code = "profile-authority-required";
  throw error;
};

export function shouldUseTimeline() {
  return true;
}

async function fetchAuthorityItems({
  batchSize = 12,
  lastDoc = null,
  organisationUid = null,
  profileUserUid = null,
  scopeOrganisation = false,
  publicOrganisationView = false,
  feedScope = "open",
  searchQuery = "",
  actorUid = null,
} = {}) {
  if (profileUserUid) profileAuthorityRequired();
  const scope = publicOrganisationView
    ? "organisation-public"
    : scopeOrganisation
      ? "organisations"
      : feedScope === "follows"
        ? "follows"
        : "open";
  const orgId = ["organisations", "organisation-public"].includes(scope)
    ? organisationUid
    : null;
  if (["organisations", "organisation-public"].includes(scope) && !orgId) {
    const error = new Error("An organisation is required for this feed.");
    error.code = "organisation-required";
    throw error;
  }
  const itemResultPromise = marketplaceFeedSource.fetchPage({
    actorUid,
    feedScope,
    scopeOrganisation,
    publicOrganisationView,
    organisationUid,
    lastDoc: normalizeCursor(lastDoc),
    batchSize: clampBatchSize(batchSize),
  });
  const mediaResultPromise =
    isMediaAuthorityReadsFeatureEnabled && scope !== "follows"
      ? mediaAuthorityService.listMediaPosts({
          scope,
          orgId,
          limit: clampBatchSize(batchSize),
        })
      : Promise.resolve({ items: [], hasMore: false, nextCursor: null });
  const [itemResult, mediaResult] = await Promise.all([
    itemResultPromise.catch((error) => {
      if (error?.code === "feature-disabled" || error?.code === "unavailable") {
        return {
          items: [],
          hasMore: false,
          lastDoc: null,
          fromAuthority: false,
          fromFollowing: false,
        };
      }
      throw error;
    }),
    mediaResultPromise.catch((error) => {
      if (error?.code === "feature-disabled" || error?.code === "unavailable") {
        return { items: [], hasMore: false, nextCursor: null };
      }
      throw error;
    }),
  ]);
  const search = normalizeSearch(searchQuery);
  const items = mergeTimelinePresentations(itemResult.items, mediaResult.items)
    .filter((item) => matchesSearch(item, search));
  return Object.freeze({
    items: Object.freeze(items),
    lastDoc: itemResult.lastDoc,
    hasMore: itemResult.hasMore || mediaResult.hasMore,
    fromTimeline: true,
    fromAuthority:
      itemResult.fromAuthority === true || isMediaAuthorityReadsFeatureEnabled,
    fromLegacyCompatibility:
      itemResult.fromLegacyCompatibility === true,
    fromFollowing: itemResult.fromFollowing === true,
    profileMode: false,
    fromCache: false,
    revalidatePromise: null,
  });
}

export async function fetchTimelineItems(options = {}) {
  return fetchAuthorityItems(options);
}

export async function fetchDetailedItemData() {
  // Detailed item data is never resolved from an arbitrary browser-supplied
  // Firestore path. Callers must use a bounded item/profile presentation.
  return null;
}

export async function ensureFreshData(timelineItem) {
  return timelineItem;
}

export async function fetchMarketplaceItems({
  filters = {},
  searchQuery = null,
  ...options
} = {}) {
  return fetchAuthorityItems({
    ...options,
    searchQuery: searchQuery ?? filters.searchQuery ?? "",
  });
}

export function normalizeTimelineItems(timelineItems) {
  if (!Array.isArray(timelineItems)) return [];
  return timelineItems.map((item) => ({
    ...item,
    id: item.id || item.itemId,
    itemName: item.itemName || item.title || "",
    itemDescription: item.itemDescription || item.description || "",
    title: item.title || item.itemName || "",
    description: item.description || item.itemDescription || "",
    images: Array.isArray(item.images) ? item.images : [],
    _authorityPresentation: item._authorityPresentation === true,
  }));
}
